import {
  AuthResponse,
  EntraCallbackRequest,
  LogoutRequest,
  MagicLinkValidateRequest,
  RegisterRequest,
  RegisterResponse,
} from '@/services/platform/api/model';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import {
  PUSH_NOTIFICATIONS_CONSENTED_KEY,
  PUSH_NOTIFICATIONS_OPT_OUT_KEY,
} from '@/lib/notifications/pushNotifications';
import {
  entraCallbackV1,
  refreshTokenV1,
  registerV1,
  logoutV1,
  validateMagicLinkV1,
} from '../../api/authentication/authentication';

/**
 * Registers a new user account.
 *
 * @param request - Registration payload (firstName, lastName, email, birthDate, phoneNumber, language, origin).
 * @returns `RegisterResponse` on HTTP 201 (success) or non-2xx with `success: false` and error details.
 * @returns `undefined` if the request throws unexpectedly (network error, timeout).
 *
 * Response status semantics:
 * - 201 — account created; `user` contains the registered user's email and name.
 * - 400 — validation failure; `field` identifies the invalid field, `message` describes the issue.
 * - 409 — conflict; email already registered — `field` is typically `"email"`.
 * - 502 — upstream failure (Entra B2C / Salesforce / CRM unreachable).
 */
export async function register(request: RegisterRequest): Promise<RegisterResponse | undefined> {
  try {
    const response = await registerV1(request);

    if (response.status === 201) {
      return response.data as RegisterResponse;
    } else {
      console.warn(`Error registering user (status: ${response.status})`, response.data);
      return response.data as RegisterResponse;
    }
  } catch (error) {
    console.warn('Error registering user:', error);
    return undefined;
  }
}

/**
 * Exchanges the stored refresh token for a new access/refresh token pair.
 *
 * Reads the refresh token from `useAuthStore`. Returns `undefined` without
 * making a network call if no refresh token is present.
 *
 * On success, updates `accessToken`, `refreshToken`, and `expiresAt` in the
 * store so subsequent authenticated calls pick up the new token immediately.
 *
 * On 401, clears the session via `clearUser()` — the stored tokens are invalid
 * and the user must re-authenticate.
 *
 * @returns `AuthResponse` on HTTP 200.
 * @returns `undefined` if unauthenticated, on 401, or on network error.
 */
export async function refreshToken(): Promise<AuthResponse | undefined> {
  try {
    const { refreshToken: storedToken, setTokens, setUser, clearUser } = useAuthStore.getState();

    if (!storedToken) {
      return undefined;
    }

    const response = await refreshTokenV1({ refreshToken: storedToken });
    const { status, data } = response as { status: number; data: unknown };

    if (status === 200) {
      const auth = data as AuthResponse;
      setTokens(auth?.accessToken ?? '', auth?.refreshToken ?? null, auth?.expiresAt ?? null);
      setUser(auth?.user ?? null);
      return auth;
    }

    if (status === 401) {
      clearUser();
      return undefined;
    }

    console.warn(`Unexpected status refreshing token (status: ${status})`);
    return undefined;
  } catch (error) {
    console.warn('Error refreshing token:', error);
    return undefined;
  }
}

/**
 * Exchanges an Entra (Azure AD External ID) id_token for a platform session.
 *
 * Posts the raw id_token received from the Entra External ID OAuth2 implicit flow to the
 * platform callback endpoint, which validates it and issues a platform
 * access/refresh token pair. On success, the tokens are written to
 * `useAuthStore` so all subsequent authenticated calls pick them up immediately.
 *
 * @param entraToken - The raw id_token returned by the Entra implicit flow.
 * @returns `AuthResponse` on HTTP 200, with `accessToken`, `refreshToken`, and `expiresAt`.
 * @returns `undefined` on 401 (invalid or expired token) or network error.
 */
export async function login(entraToken: string): Promise<AuthResponse | undefined> {
  try {
    const request: EntraCallbackRequest = { entraToken };
    const response = await entraCallbackV1(request);
    const { status, data } = response as { status: number; data: unknown };

    if (status === 200) {
      const auth = data as AuthResponse;
      const { setTokens, setUser } = useAuthStore.getState();
      setTokens(auth?.accessToken ?? '', auth?.refreshToken ?? null, auth?.expiresAt ?? null);
      setUser(auth?.user ?? null);
      return auth;
    }

    console.warn(`login: unexpected status ${status}`);
    return undefined;
  } catch (error) {
    console.warn('login: request failed:', error);
    return undefined;
  }
}

/**
 * Exchanges a magic-link token for a platform session.
 *
 * Posts the token received via the magic-link email/URL to the platform
 * validation endpoint, which verifies it and issues a platform access/refresh
 * token pair. On success, the tokens are written to `useAuthStore` so all
 * subsequent authenticated calls pick them up immediately.
 *
 * @param token - The magic-link token to validate.
 * @returns `AuthResponse` on HTTP 200, with `accessToken`, `refreshToken`, and `expiresAt`.
 * @returns `undefined` on 401 (invalid or expired token) or network error.
 */
export async function validateMagicLink(token: string): Promise<AuthResponse | undefined> {
  try {
    const request: MagicLinkValidateRequest = { token };
    const response = await validateMagicLinkV1(request);
    const { status, data } = response as { status: number; data: unknown };

    if (status === 200) {
      const auth = data as AuthResponse;
      const { setTokens, setUser } = useAuthStore.getState();
      setTokens(auth?.accessToken ?? '', auth?.refreshToken ?? null, auth?.expiresAt ?? null);
      setUser(auth?.user ?? null);
      return auth;
    }

    console.warn(`validateMagicLink: unexpected status ${status}`);
    return undefined;
  } catch (error) {
    console.warn('validateMagicLink: request failed:', error);
    return undefined;
  }
}

/**
 * Revokes the current session on the server, clears all local auth state, and
 * redirects to the app root.
 *
 * Reads the stored refresh token from `useAuthStore` and sends it to the
 * logout endpoint so the server can invalidate the token pair. `clearUser()`
 * and the redirect to `"/"` are always executed regardless of the server
 * response — local state is cleared and the user is sent to the root even if
 * the network call fails or the token is already expired.
 *
 * Uses `window.location.replace("/")` so the login page is not left in the
 * browser history and the back button cannot return to an authenticated view.
 *
 * @returns `true` on HTTP 204 (session successfully revoked on the server).
 * @returns `false` on 400/401 or network error — local state is still cleared
 *   and the redirect still occurs.
 *
 * Response status semantics:
 * - 204 — session revoked; tokens are no longer accepted by the server.
 * - 400 — malformed request.
 * - 401 — token already invalid or expired.
 */
export async function logout(): Promise<boolean> {
  const { refreshToken: storedToken, clearUser } = useAuthStore.getState();

  try {
    const request: LogoutRequest = { refreshToken: storedToken };
    const response = await logoutV1(request);
    const { status } = response as { status: number };

    if (status !== 204) {
      console.warn(`logout: unexpected status ${status}`);
    }

    return status === 204;
  } catch (error) {
    console.warn('logout: request failed:', error);
    return false;
  } finally {
    clearUser();
    // These flags belong to the device+session pair, not the account — leaving them set lets a
    // stale 'consented' from the previous session race the next session's own permission checks
    // (see useSfmcContactKeySync / resyncPushPermissionState). Whoever logs in next re-derives
    // consent from the real OS permission state on the next resync/mount.
    localStorage.removeItem(PUSH_NOTIFICATIONS_CONSENTED_KEY);
    localStorage.removeItem(PUSH_NOTIFICATIONS_OPT_OUT_KEY);
    window.location.replace('/');
  }
}
